import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ErrorBoundary, ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { ImageViewModel, PropertyInfoSectionViewModel, PropertyInfoSubSectionViewModel } from 'models';

import { UitkLayoutColumns } from 'uitk-react-layout-columns';

import { PropertyDetailsSegment } from 'components/infosite/policies/internals/property-details-segment';
import { SectionBody } from './internals/section-body';
import { SectionImageLayout } from './internals/section-image-layout';
import { Sections } from './internals/sections';

interface PropertyInfoSectionProps {
  analytics?: AnalyticsStore;
  propertyInfoSection: PropertyInfoSectionViewModel;
  isToggleHidden?: boolean;
  movePropertyTitle?: boolean;
  subSectionTagLevel?: any;
  isAmenity?: boolean;
}

export const PropertyInfoSection: React.FunctionComponent<PropertyInfoSectionProps> = ({
  analytics,
  propertyInfoSection,
  isToggleHidden = true,
  movePropertyTitle = true,
  subSectionTagLevel = 3,
  isAmenity = false
}) => {
  if (!propertyInfoSection) {
    return null;
  }

  const { dataLabel, formattedText, image, sections, title, toggleHidden } = propertyInfoSection;
  const propertyInfoProp = title && ((sections && sections.length > 0) || formattedText);

  if (!propertyInfoProp) {
    return null;
  }

  const propertyAnalyticsId = toggleHidden && toggleHidden.analyticsId;
  const propertyInfoAnalytics = analytics && propertyAnalyticsId && analytics.setupTracker(`.${propertyAnalyticsId}`, 'SeeMore_SeeLess');
  const dataStid = dataLabel && `section-${dataLabel}`;

  const titleClassName = `${dataLabel}__title policies__header--primary uitk-type-heading-600 uitk-type-bold`;
  const errorBoundarySectionLabel = `hotel.info.section.${propertyAnalyticsId}`;
  const errorBoundaryDetailsLabel = `hotel.info.details.${propertyAnalyticsId}`;
  const hasSubsections: boolean = Boolean(sections);
  const placeMainTitleOnLeftSide = title && !movePropertyTitle;
  const placeMainTitleOnRightSide = title && movePropertyTitle;

  return (
    <Viewport>
      <ViewSmall>
        <ErrorBoundary label={errorBoundarySectionLabel}>
          {placeMainTitleOnLeftSide && (
            <div className={isAmenity ? 'all-y-padding-three' : 'all-t-padding-three'}>
              <h2 className={`${titleClassName}`}>{title}</h2>
            </div>
          )}
          <PropertyDetailsSegment
            analytics={propertyInfoAnalytics}
            dataStid={dataStid}
            errorBoundaryLabel={errorBoundaryDetailsLabel}
            rootClassName={dataLabel}
            title={placeMainTitleOnRightSide && title}
            isToggleHidden={isToggleHidden}
            propertyInfoSection={true}
            isAmenity={isAmenity}
          >
            <SectionImageLayout propertyInfo={propertyInfoSection} />
            <SectionBody propertyInfoBody={formattedText} hasSubsections={hasSubsections} />
            <SectionsLayout
              dataStid={dataStid}
              splitIntoColumns={false}
              image={image}
              sections={sections}
              uitkHeadingLevel={subSectionTagLevel}
              movePropertyTitle={movePropertyTitle}
              indentContent={isAmenity}
            />
          </PropertyDetailsSegment>
        </ErrorBoundary>
      </ViewSmall>
      <ViewLarge>
        <ErrorBoundary label={errorBoundarySectionLabel}>
          <div className={`uitk-cell all-cell-1-3 all-x-padding-three ${isAmenity ? '' : 'all-y-padding-three'}`}>
            {placeMainTitleOnLeftSide && <h2 className={`${titleClassName} all-y-padding-three`}>{title}</h2>}
          </div>
          <div className="uitk-cell all-cell-2-3 all-x-padding-three">
            <PropertyDetailsSegment
              analytics={propertyInfoAnalytics}
              dataStid={dataStid}
              errorBoundaryLabel={errorBoundaryDetailsLabel}
              rootClassName={dataLabel}
              title={placeMainTitleOnRightSide && title}
              isToggleHidden={isToggleHidden}
              propertyInfoSection={true}
            >
              <SectionImageLayout propertyInfo={propertyInfoSection} />
              <SectionBody propertyInfoBody={formattedText} hasSubsections={hasSubsections} />
              <SectionsLayout
                dataStid={dataStid}
                splitIntoColumns={isAmenity}
                image={image}
                sections={sections}
                uitkHeadingLevel={subSectionTagLevel}
                movePropertyTitle={movePropertyTitle}
                indentContent={isAmenity}
              />
            </PropertyDetailsSegment>
          </div>
        </ErrorBoundary>
      </ViewLarge>
    </Viewport>
  );
};

PropertyInfoSection.displayName = 'PropertyInfoSection';

interface SectionsLayoutProps {
  dataStid: string;
  splitIntoColumns?: boolean;
  image?: ImageViewModel;
  sections?: PropertyInfoSubSectionViewModel[];
  uitkHeadingLevel?: any;
  movePropertyTitle?: boolean;
  indentContent?: boolean;
}

export const SectionsLayout = ({
  splitIntoColumns,
  image,
  sections,
  dataStid,
  uitkHeadingLevel,
  movePropertyTitle,
  indentContent
}: SectionsLayoutProps) => {
  if (image || !sections) {
    return null;
  }

  return (
    <>
      {splitIntoColumns && (
        <SectionColumns
          sections={sections}
          dataStid={dataStid}
          uitkHeadingLevel={uitkHeadingLevel}
          movePropertyTitle={movePropertyTitle}
          indentContent={indentContent}
        />
      )}
      {!splitIntoColumns && (
        <SectionsContent
          sections={sections}
          dataStid={dataStid}
          uitkHeadingLevel={uitkHeadingLevel}
          movePropertyTitle={movePropertyTitle}
          indentContent={indentContent}
        />
      )}
    </>
  );
};

export const SectionColumns = ({ sections, dataStid, uitkHeadingLevel, movePropertyTitle, indentContent }) => (
  <UitkLayoutColumns columns={2} space="twelve">
    <SectionsContent
      sections={sections}
      dataStid={dataStid}
      uitkHeadingLevel={uitkHeadingLevel}
      movePropertyTitle={movePropertyTitle}
      indentContent={indentContent}
    />
  </UitkLayoutColumns>
);

export const SectionsContent = ({ sections, dataStid, uitkHeadingLevel, movePropertyTitle, indentContent = false }) => (
  <>
    {sections.map((section, index) => (
      <Sections
        key={`${dataStid}-${index}`}
        section={section}
        dataStid={dataStid}
        uitkHeadingLevel={uitkHeadingLevel}
        movePropertyTitle={movePropertyTitle}
        indentContent={indentContent}
      />
    ))}
  </>
);

SectionsLayout.displayName = 'SectionsLayout';
